import { useEffect, useState } from 'react'
import { ref, uploadBytes, listAll, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage, auth } from '../../../../firebase'
import { onAuthStateChanged } from 'firebase/auth'
import {
  X,
  UploadSimple,
  CheckCircle,
  WarningCircle,
  Spinner,
  FileText,
  Trash,
} from '@phosphor-icons/react'
import { Heading, Subheading } from '../../../../Components/Screen/Text/Heading'
import { Subtext } from '../../../../Components/Screen/Text/Body'
import { Button, ButtonOutline } from '../../../../Components/Screen/Button/button'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx,application/pdf,image/png,image/jpeg,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'

function basePath(nftId) {
  return `demonstrativos/${nftId}`
}

async function fetchDemonstrativos(nftId) {
  const dirRef = ref(storage, basePath(nftId))
  const list = await listAll(dirRef)
  return Promise.all(
    list.items.map(async (item) => ({
      name: item.name,
      fullPath: item.fullPath,
      url: await getDownloadURL(item),
    }))
  )
}

export function ModalDemonstrativo({ nftId, onClose }) {
  const [file, setFile] = useState(null)
  const [items, setItems] = useState(null) // null = loading
  const [status, setStatus] = useState(null) // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('')

  const reload = async () => {
    try {
      const list = await fetchDemonstrativos(nftId)
      setItems(list)
    } catch (err) {
      setItems([])
      setErrorMsg(err.message || 'Erro ao listar arquivos')
    }
  }

  useEffect(() => {
    if (!nftId) return
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return
      reload()
      unsubscribe()
    })
    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftId])

  const handleUpload = async () => {
    if (!file) return
    setStatus('loading')
    setErrorMsg('')
    try {
      const storageRef = ref(storage, `${basePath(nftId)}/${Date.now()}-${file.name}`)
      await uploadBytes(storageRef, file, { contentType: file.type })
      setFile(null)
      await reload()
      setStatus('success')
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao fazer upload')
      setStatus('error')
    }
  }

  const handleDelete = async (fullPath) => {
    try {
      await deleteObject(ref(storage, fullPath))
      await reload()
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao excluir arquivo')
      setStatus('error')
    }
  }

  return (
    <div className="modal modal-open backdrop-blur">
      <div className="relative modal-box bg-preto border border-cinzaescuro max-w-lg w-full flex flex-col gap-5">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-cinza hover:text-branco transition"
        >
          <X size={22} />
        </button>

        <div>
          <Heading>Demonstrativo</Heading>
          <Subtext className="text-cinza">
            Envie ou baixe os demonstrativos da propriedade
          </Subtext>
        </div>

        <div className="flex flex-col gap-2">
          <Subheading>Arquivos enviados</Subheading>
          {items === null ? (
            <div className="flex items-center gap-2 text-cinza">
              <Spinner size={18} className="animate-spin" />
              <Subtext className="text-cinza">Carregando...</Subtext>
            </div>
          ) : items.length === 0 ? (
            <Subtext className="text-cinza">Nenhum demonstrativo enviado ainda.</Subtext>
          ) : (
            <ul className="flex flex-col gap-1">
              {items.map((item) => (
                <li
                  key={item.fullPath}
                  className="flex items-center justify-between gap-2 px-3 py-2 border border-cinzaescuro rounded-lg"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <FileText size={18} className="text-verdeClaro shrink-0" />
                    <Subtext className="truncate text-branco">{item.name}</Subtext>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.fullPath)}
                    className="text-cinza hover:text-red-400 transition shrink-0"
                    title="Excluir"
                  >
                    <Trash size={16} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Subheading>Enviar novo demonstrativo</Subheading>
          <label className="flex items-center gap-3 border border-cinzaescuro rounded-xl px-4 py-3 cursor-pointer hover:border-verdeClaro transition">
            <UploadSimple size={20} className="text-verdeClaro shrink-0" />
            <Subtext className="truncate text-cinza">
              {file ? file.name : 'Selecionar arquivo (PDF, imagem ou DOC)'}
            </Subtext>
            <input
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => setFile(e.target.files[0] || null)}
            />
          </label>
        </div>

        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-400">
            <WarningCircle size={18} />
            <Subtext className="text-red-400">{errorMsg}</Subtext>
          </div>
        )}

        {status === 'success' && (
          <div className="flex items-center gap-2 text-verdeClaro">
            <CheckCircle size={18} />
            <Subtext className="text-verdeClaro">Upload concluído!</Subtext>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <ButtonOutline onClick={onClose}>Fechar</ButtonOutline>
          <Button onClick={handleUpload} disabled={!file || status === 'loading'}>
            {status === 'loading' ? (
              <span className="flex items-center gap-2">
                <Spinner size={16} className="animate-spin" />
                Enviando...
              </span>
            ) : (
              'Enviar arquivo'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
